/* Поле даты с выпадающим календарём.
 *
 * Значение остаётся строкой «ДД.ММ.ГГГГ» — ровно тем, что ходит в командах и
 * реестрах (сервер парсит строгим dd.MM.yyyy). Ручной ввод сохранён: операторы
 * реестров набирают даты быстрее, чем кликают. Календарь — Radix Popover c
 * порталом: диалоги (DlgShell, Radix Dialog) режут содержимое overflow'ом, и
 * absolute-див внутри них был бы обрезан.
 *
 * Ошибка формата и «дата в прошлом» показываются по blur, а не на каждый
 * символ: недописанная дата — не ошибка. Проверка мягкая и клиентская, сервер
 * остаётся последней инстанцией (400 date.invalid).
 */
import { useEffect, useMemo, useState } from 'react'
import * as Popover from '@radix-ui/react-popover'
import { Input } from './forms'
import { Icon } from '@/ds/icons'
import { fmtRu } from '@/lib/format'
import { t, type Lang } from '@/i18n/lang'

/** Строгий разбор «ДД.ММ.ГГГГ»: JS-Date перекатывает 32.13 в следующий месяц,
    а перекат — это принятая молча ошибка ввода. Двузначный год принимается
    тоже: он остался в файлах, заполненных до перехода на четыре знака. */
function parseStrict(v: string): Date | null {
  const m = /^(\d{1,2})\.(\d{1,2})\.(\d{2}|\d{4})$/.exec(v.trim())
  if (!m) return null
  const year = m[3]!.length === 2 ? 2000 + Number(m[3]) : Number(m[3])
  const d = new Date(year, Number(m[2]) - 1, Number(m[1]))
  return d.getMonth() === Number(m[2]) - 1 && d.getDate() === Number(m[1]) ? d : null
}

const floorDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate())

const LOCALE: Record<Lang, string> = { ru: 'ru-RU', en: 'en-GB', zh: 'zh-CN' }

/* Календарь ходит по тому же диапазону, что переживает round-trip строки:
   год пишется четырьмя знаками, но границы остаются прежними. */
const YEAR_MIN = 2000
const YEAR_MAX = 2099

function CalendarGrid({ lang, value, minDate, onPick }: {
  lang: Lang
  value: Date | null
  minDate?: Date
  onPick: (d: Date) => void
}) {
  const [view, setView] = useState<Date>(() => {
    const base = value ?? new Date()
    return new Date(base.getFullYear(), base.getMonth(), 1)
  })
  const locale = LOCALE[lang]
  const monthTitle = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(view),
    [locale, view],
  )
  // Неделя с понедельника: 05.01.2026 — понедельник.
  const weekdays = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short' })
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2026, 0, 5 + i)))
  }, [locale])

  const min = minDate ? floorDay(minDate) : null
  const first = new Date(view.getFullYear(), view.getMonth(), 1)
  const lead = (first.getDay() + 6) % 7
  const cells = Array.from({ length: 42 }, (_, i) =>
    new Date(view.getFullYear(), view.getMonth(), 1 - lead + i))
  const shift = (months: number) => {
    const next = new Date(view.getFullYear(), view.getMonth() + months, 1)
    if (next.getFullYear() < YEAR_MIN || next.getFullYear() > YEAR_MAX) return
    setView(next)
  }
  const same = (a: Date, b: Date | null) => b != null && a.getTime() === floorDay(b).getTime()
  const todayDay = floorDay(new Date())

  const navBtn = (dir: -1 | 1, icon: string) => (
    <button type="button" aria-label={dir < 0 ? 'prev month' : 'next month'} onClick={() => shift(dir)}
            className="icon-btn"
            style={{ display: 'grid', placeItems: 'center', width: 26, height: 26, border: 0, background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', borderRadius: 'var(--radius-sm)' }}>
      <Icon name={icon} size={15} />
    </button>
  )
  return (
    <div style={{ width: 7 * 34 + 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
        {navBtn(-1, 'chevron-left')}
        <span style={{ flex: 1, textAlign: 'center', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-strong)' }}>{monthTitle}</span>
        {navBtn(1, 'chevron-right')}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 34px)' }}>
        {weekdays.map((w) => (
          <span key={w} style={{ textAlign: 'center', font: 'var(--font-caption)', color: 'var(--text-subtle)', padding: '2px 0' }}>{w}</span>
        ))}
        {cells.map((day) => {
          const off = day.getMonth() !== view.getMonth()
          const gone = (min != null && day < min) || day.getFullYear() < YEAR_MIN || day.getFullYear() > YEAR_MAX
          const picked = same(day, value)
          const isToday = day.getTime() === todayDay.getTime()
          return (
            <button key={day.getTime()} type="button" disabled={gone}
                    className="lav-cal-day" onClick={() => onPick(day)}
                    style={{
                      height: 30, border: isToday && !picked ? '1px solid var(--accent)' : '1px solid transparent',
                      borderRadius: 'var(--radius-sm)',
                      /* Фон невыбранного дня живёт в классе: инлайновый
                         transparent глушил бы hover. Выбранный красится
                         инлайном — и потому hover его не перебивает. */
                      background: picked ? 'var(--accent)' : undefined,
                      color: picked ? 'var(--on-accent)' : gone ? 'var(--text-subtle)' : off ? 'var(--text-muted)' : 'var(--text-body)',
                      opacity: gone ? 0.45 : 1,
                      font: 'var(--font-ui)', fontSize: 'var(--text-sm)',
                      cursor: gone ? 'default' : 'pointer',
                    }}>
              {day.getDate()}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export function DateInput({ label, hint, value, onChange, minDate, lang = 'ru', size = 'md', placeholder = 'дд.мм.гг', block = true }: {
  label?: string
  hint?: string
  /** Строка «ДД.ММ.ГГГГ» либо пустая — как в командах. */
  value: string
  onChange: (v: string) => void
  /** Раньше этого дня выбрать нельзя; ручной ввод раньше — предупреждение. */
  minDate?: Date
  lang?: Lang
  size?: 'sm' | 'md' | 'lg'
  placeholder?: string
  block?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [touched, setTouched] = useState(false)

  const trimmed = value.trim()
  const parsed = parseStrict(trimmed)
  const invalid = trimmed !== '' && trimmed !== '—' && parsed == null
  const inPast = parsed != null && minDate != null && floorDay(parsed) < floorDay(minDate)
  const error = touched && invalid
    ? t(lang, 'Ожидается дата ДД.ММ.ГГГГ', 'Expected DD.MM.YYYY')
    : touched && inPast
      ? t(lang, 'Дата в прошлом', 'Date is in the past')
      : undefined

  /* Escape при открытом календаре закрывает только календарь. DlgShell слушает
     Escape на window (bubble) — перехват на capture-фазе не даёт одной клавише
     закрыть и календарь, и диалог разом. */
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); setOpen(false) }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open])

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Anchor asChild={false} style={{ display: block ? 'block' : 'inline-block' }}>
        <Input
          label={label} hint={hint} error={error} size={size} block={block}
          value={value} placeholder={placeholder}
          onChange={(e) => { setTouched(false); onChange(e.target.value) }}
          onBlur={() => setTouched(true)}
          trailing={
            <Popover.Trigger asChild>
              <button type="button" aria-label={t(lang, 'Открыть календарь', 'Open calendar')}
                      className="icon-btn"
                      style={{ display: 'grid', placeItems: 'center', width: 24, height: 24, border: 0, background: 'transparent', color: 'var(--text-subtle)', cursor: 'pointer', borderRadius: 'var(--radius-sm)' }}>
                <Icon name="calendar-days" size={15} />
              </button>
            </Popover.Trigger>
          }
        />
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content side="bottom" align="start" sideOffset={6} className="lav-cal-pop"
                         onOpenAutoFocus={(e) => e.preventDefault()}
                         style={{ zIndex: 'var(--z-toast)' as never, padding: 8, background: 'var(--surface-raised)', border: 'var(--border-hairline) solid var(--border-default)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)' }}>
          <CalendarGrid lang={lang} value={parsed} minDate={minDate}
                        onPick={(d) => { setTouched(false); onChange(fmtRu(d)); setOpen(false) }} />
        </Popover.Content>
      </Popover.Portal>
      {/* Той же анимацией открываются поповеры фильтров таблиц (tooltip-in в ui.css). */}
      <style>{`.lav-cal-pop {
        transform-origin: var(--radix-popover-content-transform-origin);
        animation: tooltip-in var(--duration-fast) var(--ease-out);
      }
      /* Дни календаря отвечают на наведение, как соседние кнопки навигации
         месяца. Hover — только под указателем: на касании он липнет. */
      .lav-cal-day { background: transparent; transition: background var(--duration-fast) var(--ease-standard); }
      @media (hover: hover) and (pointer: fine) {
        .lav-cal-day:not(:disabled):hover { background: var(--surface-hover); }
      }`}</style>
    </Popover.Root>
  )
}
